import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { extractErrorMessage } from '../../utils/errorUtils';
import { getCurrentLocalMonthDateRange } from '../../components/common/customDatePicker.utils';
import { formatMoney } from '../reports/formatters';
import { ExpenseCategoryReportDetailsDialog } from './ExpenseCategoryReportDetailsDialog';
import { useGetOptionsQuery, useLazyGenerateReportQuery } from './api/expenseCategoryReportApi';
import {
  exportExpenseCategoryReportExcel,
  exportExpenseCategoryReportPdf,
} from './exportExpenseCategoryReport';
import type {
  ExpenseCategoryFilterOption,
  ExpenseCategoryReportBankAccountOption,
  ExpenseCategoryReportRequest,
  ExpenseCategoryReportRow,
} from './types';

const currentMonth = () => getCurrentLocalMonthDateRange();

const unclassifiedOption: ExpenseCategoryFilterOption = {
  id: null,
  name: 'Unclassified',
  isInactive: false,
  isUnclassified: true,
};

export const ExpenseCategoryReportPage = () => {
  const defaults = useMemo(currentMonth, []);
  const { data: options, isLoading: optionsLoading, error: optionsError } = useGetOptionsQuery();
  const [generateReport, { data: report, isFetching, error: reportError, reset }] =
    useLazyGenerateReportQuery();
  const [dateFrom, setDateFrom] = useState(defaults.from);
  const [dateTo, setDateTo] = useState(defaults.to);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [bankAccounts, setBankAccounts] = useState<ExpenseCategoryReportBankAccountOption[]>([]);
  const [categories, setCategories] = useState<ExpenseCategoryFilterOption[]>([]);
  const [detailRow, setDetailRow] = useState<ExpenseCategoryReportRow | null>(null);
  const initialized = useRef(false);

  const companyOptions = options?.companies ?? [];
  const accountOptions = useMemo(
    () =>
      (options?.bankAccounts ?? []).filter(
        (account) => !companyId || account.companyId === companyId
      ),
    [companyId, options?.bankAccounts]
  );
  const categoryOptions = useMemo<ExpenseCategoryFilterOption[]>(
    () => [unclassifiedOption, ...(options?.categories ?? [])],
    [options?.categories]
  );
  const selectedCompany = companyOptions.find((company) => company.id === companyId) ?? null;

  const request = (): ExpenseCategoryReportRequest => ({
    DateFrom: dateFrom,
    DateTo: dateTo,
    CompanyId: companyId,
    BankAccountIds: bankAccounts.length ? bankAccounts.map((account) => account.id) : null,
    ExpenseCategoryIds: categories.length
      ? categories.flatMap((category) => (category.id == null ? [] : [category.id]))
      : null,
    IncludeUnclassified:
      categories.length === 0 || categories.some((category) => category.isUnclassified),
  });

  useEffect(() => {
    if (initialized.current || optionsLoading) return;
    initialized.current = true;
    void generateReport(request());
    // Initial report intentionally uses the initial local-month filters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generateReport, optionsLoading]);

  const clear = () => {
    const period = currentMonth();
    setDateFrom(period.from);
    setDateTo(period.to);
    setCompanyId(null);
    setBankAccounts([]);
    setCategories([]);
    setDetailRow(null);
    reset();
  };

  const filterLabels = () => [
    `Period: ${dateFrom} вЂ” ${dateTo}`,
    `Company: ${selectedCompany?.name ?? 'All'}`,
    `Bank Accounts: ${bankAccounts.length ? bankAccounts.map((item) => item.name).join(', ') : 'All'}`,
    `Expense Categories: ${categories.length ? categories.map((item) => item.name).join(', ') : 'All'}`,
  ];

  const loading = optionsLoading || isFetching;

  return (
    <Box
      component="section"
      aria-label="Expenses by Category Report"
      sx={{ p: { xs: 1, md: 2 }, height: '100%', overflow: 'auto' }}
    >
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="flex-end">
          <TextField
            label="From"
            type="date"
            value={dateFrom}
            onChange={(event) => setDateFrom(event.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={loading}
          />
          <TextField
            label="To"
            type="date"
            value={dateTo}
            onChange={(event) => setDateTo(event.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={loading}
          />
          <Autocomplete
            sx={{ minWidth: 220 }}
            options={companyOptions}
            value={selectedCompany}
            onChange={(_event, value) => {
              setCompanyId(value?.id ?? null);
              setBankAccounts([]);
            }}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disabled={loading}
            renderInput={(params) => <TextField {...params} label="Company" placeholder="All" />}
          />
          <Autocomplete
            multiple
            sx={{ minWidth: 280 }}
            options={accountOptions}
            value={bankAccounts}
            onChange={(_event, value) => setBankAccounts(value)}
            getOptionLabel={(option) => `${option.name}${option.isInactive ? ' (Inactive)' : ''}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disabled={loading}
            renderInput={(params) => (
              <TextField {...params} label="Bank Accounts" placeholder="All" />
            )}
          />
          <Autocomplete
            multiple
            sx={{ minWidth: 280 }}
            options={categoryOptions}
            value={categories}
            onChange={(_event, value) => setCategories(value)}
            getOptionLabel={(option) => `${option.name}${option.isInactive ? ' (Inactive)' : ''}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disabled={loading}
            renderInput={(params) => (
              <TextField {...params} label="Expense Categories" placeholder="All" />
            )}
          />
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button
              variant="contained"
              disabled={loading || !dateFrom || !dateTo || dateFrom > dateTo}
              onClick={() => void generateReport(request())}
            >
              {isFetching ? 'Loading...' : 'Generate Report'}
            </Button>
            <Button variant="outlined" disabled={loading} onClick={clear}>
              Clear
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {(optionsError || reportError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {extractErrorMessage(
            reportError || optionsError,
            'Failed to load expense category report.'
          )}
        </Alert>
      )}
      {loading && !report && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {report && (
        <Paper variant="outlined">
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Expenses from {report.dateFrom.slice(0, 10)} to {report.dateTo.slice(0, 10)}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                disabled={!report.rows.length}
                onClick={() => void exportExpenseCategoryReportExcel(report, filterLabels())}
              >
                Export Excel
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={!report.rows.length}
                onClick={() => void exportExpenseCategoryReportPdf(report, filterLabels())}
              >
                Export PDF
              </Button>
            </Stack>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Expense Category</TableCell>
                  <TableCell align="right">Transactions</TableCell>
                  <TableCell align="right">Amount EUR</TableCell>
                  <TableCell align="right">% of Total Expenses</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.rows.length ? (
                  report.rows.map((row) => (
                    <TableRow key={row.categoryId ?? 'unclassified'} hover>
                      <TableCell>
                        <Link
                          component="button"
                          underline="hover"
                          onClick={() => setDetailRow(row)}
                        >
                          {row.category}
                        </Link>{' '}
                        {row.isInactive && <Chip size="small" label="Inactive" />}
                      </TableCell>
                      <TableCell align="right">{row.transactionCount}</TableCell>
                      <TableCell align="right">{formatMoney(row.amountEur)} EUR</TableCell>
                      <TableCell align="right">{formatMoney(row.percentage)}%</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No data
                    </TableCell>
                  </TableRow>
                )}
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {report.totals.transactionCount}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {formatMoney(report.totals.amountEur)} EUR
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {formatMoney(report.totals.percentage)}%
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      <ExpenseCategoryReportDetailsDialog
        key={detailRow?.categoryId ?? (detailRow?.isUnclassified ? 'unclassified' : 'closed')}
        row={detailRow}
        onClose={() => setDetailRow(null)}
      />
    </Box>
  );
};
